/**
 * React layer over the framework-free checks (03-07 §3). The app injects the subject — the
 * session's effective permissions — via the provider; the package still never fetches and stays
 * auth-free.
 */

import { createContext, createElement, Fragment, useContext, useMemo, type ReactNode } from 'react';

import { canAction, type ActionId } from './actions';
import { hasPermission, type Subject } from './checks';
import type { PermissionKey } from './matrix.config';
import { canAccessRoute, type RouteId } from './routes';

export interface PermissionApi {
  /**
   * Whether the current subject may perform `actionId`, per `ACTION_PERMISSIONS`.
   *
   * It takes a UI action rather than a backend permission key on purpose. A control that names its
   * key directly cannot survive that key being renamed or split — and it does not even record what
   * it meant, so the split cannot be done mechanically. `ACTION_PERMISSIONS` is the one place that
   * knows, and it is where a backend vocabulary change lands.
   *
   * It used to take an optional `scope`, accepted for forward-compat and ignored. HS-1387
   * removed it along with the rest of the never-populated scope model: an argument that never
   * affected a result reads at every call site as though scope were being enforced here, and it
   * never was. Scope enforcement is the server's job (03-07 §4).
   *
   * UI show/hide only — the server remains authoritative.
   */
  can: (actionId: ActionId) => boolean;
  /**
   * Whether the current subject may open `routeId`, per `ROUTE_PERMISSIONS`.
   *
   * A route usually grants on *any one* of several keys, which `can` cannot express without the
   * caller re-listing them — and a navigation menu that re-lists them is a second copy of the map
   * that drifts. Callers that hide a nav entry and the guard that refuses the route must read the
   * same entry, so both go through here.
   */
  canRoute: (routeId: RouteId) => boolean;
  /**
   * Whether the current subject holds a raw catalogue key.
   *
   * **Use `can` instead.** This exists for the two cases where the key is *data* rather than a
   * fixed gate, and an `ActionId` therefore cannot exist:
   *
   * - a screen that asks "may the caller grant THIS key to someone else", where the key arrives
   *   from the server at runtime (`useUserPermissionManagement`);
   * - `@smartsash/data-screen`'s form buttons, which read the same `permissions` block its route
   *   guards do, and route guards are keyed on the catalogue by design.
   *
   * Reaching for it to gate an ordinary control puts a backend key back at a call site, which is
   * the coupling `ACTION_PERMISSIONS` exists to remove.
   */
  canKey: (key: PermissionKey) => boolean;
}

const PermissionContext = createContext<PermissionApi | null>(null);

export interface PermissionsProviderProps {
  /** The session's effective permission keys. HS-1387 removed the `roles`/`scopes` props. */
  permissions?: readonly PermissionKey[];
  children: ReactNode;
}

export function PermissionsProvider({ permissions, children }: PermissionsProviderProps) {
  const api = useMemo<PermissionApi>(() => {
    const subject: Subject = permissions ? { permissions } : {};
    return {
      can: (actionId) => canAction(actionId, subject),
      canRoute: (routeId) => canAccessRoute(routeId, subject),
      canKey: (key) => hasPermission(subject, key),
    };
  }, [permissions]);

  return createElement(PermissionContext.Provider, { value: api }, children);
}

export function usePermission(): PermissionApi {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermission must be used within a <PermissionsProvider>');
  return ctx;
}

export interface CanProps {
  /** A UI action, not a backend permission key — see `can` above and `ACTION_PERMISSIONS`. */
  action: ActionId;
  /** Rendered when the subject may not perform `action`. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

export function Can({ action, fallback = null, children }: CanProps) {
  const { can } = usePermission();
  return createElement(Fragment, null, can(action) ? children : fallback);
}
